import React, { useEffect, useState } from "react";
import { Box, Typography, Paper, Button, Divider, Grid } from "@mui/material";
import { useTheme } from "../../ThemeContext";
import BillingService from "./billing-service";
import { getStripeProducts } from "../../utils/apiClient";
import planFeatures from "./plan-features.json";
import EnterpriseContactModal from "./EnterpriseContactModal";
import LogoLoader from "../common/loaders/gif-logo-loader";

// Optional mapping for alternate product names (extend if backend names change)
const PLAN_NAME_MAP = {
  "VoiceCare Free Plan": "Per Minute Based Free",
  "VoiceCare Startup Plan": "Per Minute Based Startup",
  "VoiceCare Pro Plan": "Per Minute Based Pro",
  "VoiceCare Scale Plan": "Per Minute Based Scale",
  "VoiceCare Enterprise Plan": "Per Minute Based Enterprise",
};

// Order in which plans should appear in the UI
const PLAN_ORDER = [
  "VoiceCare Free Plan",
  "VoiceCare Startup Plan",
  "VoiceCare Pro Plan",
  "VoiceCare Scale Plan",
  "VoiceCare Enterprise Plan",
];

const PlansAndLimits = ({ usage, planName, email }) => {
  const { currentTheme, themeMode } = useTheme();
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(planName);
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [error, setError] = useState(null);
  const [plansLoading, setPlansLoading] = useState(true);
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const [confirmingPlan, setConfirmingPlan] = useState(null);

  useEffect(() => {
    async function fetchPlans() {
      setPlansLoading(true);
      try {
        const response = await getStripeProducts("MINUTE");
        if (response && response.products) {
          // Sort plans according to PLAN_ORDER
          const sortedPlans = response.products.sort(
            (a, b) => PLAN_ORDER.indexOf(a.name) - PLAN_ORDER.indexOf(b.name)
          );
          setPlans(sortedPlans);
        }
      } catch (error) {
        console.error("Error fetching plans:", error);
        setError("Failed to load plans. Please try again.");
      } finally {
        setPlansLoading(false);
      }
    }

    fetchPlans();
    setCurrentPlan(planName);
  }, [planName]);

  const handleChoosePlan = async (plan) => {
    setError(null);
    setLoadingPlanId(plan.id);
    try {
      // Use regular checkout flow for all plans
      const successUrl =
        window.location.origin +
        "/billing?tab=plans&checkout=success&session_id={CHECKOUT_SESSION_ID}";
      const cancelUrl =
        window.location.origin + "/billing?tab=plans&checkout=cancel";
      const result = await BillingService.createSubscriptionCheckoutSession(
        plan.id,
        successUrl,
        cancelUrl
      );
      if (result && result.url) {
        window.location.href = result.url;
      } else {
        setError(result.message || "Could not start checkout session.");
      }
    } catch (e) {
      setError(e.message || "Unexpected error.");
    } finally {
      setLoadingPlanId(null);
    }
  };

  const handleContactUs = () => {
    setShowEnterpriseModal(true);
  };

  const handleCloseEnterpriseModal = () => {
    setShowEnterpriseModal(false);
  };

  // Include all plans including Free plan
  const filteredPlans = plans;

  // Helper to detect plan types based on metadata / flags
  const isFreePlan = (plan) =>
    plan.metadata?.plan_type === "free" ||
    plan.name.toLowerCase().includes("free");
  const isEnterprisePlan = (plan) =>
    plan.is_enterprise ||
    plan.metadata?.plan_type === "enterprise" ||
    plan.name.toLowerCase().includes("enterprise");

  // Helper to get the base price (licensed) and overage price (metered)
  const getBasePrice = (plan) => {
    // Handle free plan
    if (isFreePlan(plan)) {
      return "Free";
    }

    // Use base_unit_amount from the plan if available
    if (plan.base_unit_amount !== undefined && plan.base_unit_amount !== null) {
      return `$${(plan.base_unit_amount / 100).toFixed(2)}`;
    }

    // Fallback to prices array
    const priceObj = plan.prices.find(
      (p) => p.recurring?.usage_type === "licensed" && p.unit_amount !== null
    );
    return priceObj
      ? `$${(priceObj.unit_amount / 100).toFixed(2)}`
      : "Contact us";
  };

  const getOveragePrice = (plan, details) => {
    // Handle free plan
    if (isFreePlan(plan)) {
      return "N/A";
    }

    // Check if this plan has enterprise details with rate_per_unit
    if (plan.enterprise_details?.rate_per_unit) {
      return `$${plan.enterprise_details.rate_per_unit.toFixed(3)}`;
    }

    // Try to get from metered price in the prices array
    const meteredObj = plan.prices.find(
      (p) => p.recurring?.usage_type === "metered" && p.unit_amount !== null
    );
    if (meteredObj) {
      return `$${(meteredObj.unit_amount / 100).toFixed(3)}`;
    }

    // Try to get from plan-features.json if present
    if (details.price_per_minute && details.price_per_minute !== "Unlimited") {
      return `$${Number(details.price_per_minute).toFixed(3)}`;
    }

    // For enterprise plans with no pricing or empty prices array, show contact us
    if (
      isEnterprisePlan(plan) &&
      (plan.prices.length === 0 ||
        plan.enterprise_details?.rate_per_unit === null)
    ) {
      return "Contact us";
    }

    // For enterprise without specific pricing, show contact us
    if (isEnterprisePlan(plan)) return "Contact us";

    return "-";
  };

  const getIncludedUnits = (plan, details) => {
    // Handle free plan
    if (isFreePlan(plan)) {
      return details.included_minutes || "Limited";
    }

    // Use included_units from the plan if available
    if (plan.included_units !== undefined && plan.included_units !== null) {
      return plan.included_units;
    }

    // If it's enterprise with null included_units, show contact us
    if (isEnterprisePlan(plan) && plan.included_units === null) {
      return "Contact us";
    }

    // Fallback to plan-features.json (details param already resolved)
    const isEnterprise = isEnterprisePlan(plan);
    return isEnterprise ? "Unlimited" : details.included_minutes;
  };

  // Helper to prettify plan names for display (works for both naming conventions)
  const getPlanDisplayName = (name) =>
    name
      .replace(/^Per Minute Based\s+/i, "")
      .replace(/^VoiceCare\s+/i, "")
      .replace(/\s*Plan$/i, "")
      .trim();

  // Render a single row of cards
  const renderPlanCard = (plan) => {
    const legacyPlanKey = PLAN_NAME_MAP[plan.name] || plan.name;
    const details = planFeatures.plans[legacyPlanKey] || {};
    const isEnterprise = isEnterprisePlan(plan);
    const isFree = isFreePlan(plan);
    const price = getBasePrice(plan);
    const overage = getOveragePrice(plan, details);
    const units = getIncludedUnits(plan, details);
    let term;
    if (isEnterprise) {
      const months = plan.enterprise_details?.contract_term_months;
      term = months ? `${months} months` : "Custom";
    } else {
      term = isFree ? "N/A" : details.term;
    }

    return (
      <Box
        key={plan.id}
        sx={{
          marginTop: 4,
          display: "flex",
          justifyContent: "center",
          flex: "1 1 220px",
          minWidth: { xs: 140, sm: 180, md: 200, lg: 220, xl: 260 },
          maxWidth: { xs: 180, sm: 220, md: 260, lg: 300, xl: 340 },
          mx: { xs: 0.5, sm: 1, md: 2 },
          mb: { xs: 2, md: 0 },
          "@media (max-width:1400px)": {
            minWidth: 160,
            maxWidth: 200,
          },
          "@media (max-width:1200px)": {
            minWidth: 120,
            maxWidth: 160,
          },
        }}
      >
        <Paper
          sx={{
            // background: currentTheme.callhistory.bgLite,
            background: themeMode === "dark" ? "#04002b" : "#f7f7f7",
            borderRadius: 3,
            p: { xs: 1, sm: 1, md: 2, lg: 2.5 },
            width: "100%",
            height: "85%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            border:
              currentPlan === plan.name
                ? "2px solid #FF5C5C"
                : `2px solid ${themeMode === "dark" ? "#252144" : "#E2E2E2"}`,
            boxShadow:
              currentPlan === plan.name ? "0 0 12px #ff5c5c33" : "none",
            fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: themeMode === "dark" ? "#ffffff" : "#000000",
              fontWeight: 700,
              mb: 2,
              letterSpacing: 1,
              fontSize: { xs: "1.1rem", sm: "1.2rem", md: "1.3rem" },
            }}
          >
            {getPlanDisplayName(plan.name)}
          </Typography>
          <Divider sx={{ width: "100%", borderColor: "#3B3757", mb: 2 }} />
          <Box width="100%" mb={2}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography
                variant="body1"
                sx={{ color: themeMode === "dark" ? "#ffffff" : "#000000" }}
              >
                Price
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: themeMode === "dark" ? "#ffffff" : "#000000" }}
              >
                {price}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography
                variant="body1"
                sx={{ color: themeMode === "dark" ? "#ffffff" : "#000000" }}
              >
                Units
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: themeMode === "dark" ? "#ffffff" : "#000000" }}
              >
                {units}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography
                variant="body1"
                sx={{ color: themeMode === "dark" ? "#ffffff" : "#000000" }}
              >
                Price per unit
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: themeMode === "dark" ? "#ffffff" : "#000000" }}
              >
                {overage}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography
                variant="body1"
                sx={{ color: themeMode === "dark" ? "#ffffff" : "#000000" }}
              >
                Term
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: themeMode === "dark" ? "#ffffff" : "#000000" }}
              >
                {term}
              </Typography>
            </Box>
          </Box>
          {console.log(currentPlan === plan.name, "currentPlanDebugger")}
          {currentPlan === plan.name ? (
            <Button
              variant="outlined"
              disabled
              sx={{
                borderColor: "#FF5C5C",
                color: "#FF5C5C",
                width: "100%",
                fontWeight: 600,
                mt: 1,
                "&.Mui-disabled": {
                  borderColor: "#FF5C5C",
                  color: "#FF5C5C",
                },
              }}
            >
              Current Plan
            </Button>
          ) : isFree ? (
            <Button
              variant="outlined"
              disabled
              sx={{
                borderColor: "#FF5C5C",
                color: "#FF5C5C",
                width: "100%",
                fontWeight: 600,
                mt: 1,
                "&.Mui-disabled": {
                  borderColor: "#FF5C5C",
                  color: "#FF5C5C",
                },
              }}
            >
              Free Plan
            </Button>
          ) : plan.prices.length === 0 ? (
            <Button
              variant="contained"
              sx={{
                background: "#FE4E3A",
                color: "#fff",
                width: "100%",
                fontWeight: 600,
                mt: 1,
                "&:hover": {
                  background: "#FE4E3A",
                  color: "#fff",
                },
              }}
              onClick={handleContactUs}
            >
              Contact Us
            </Button>
          ) : (
            <Button
              variant="contained"
              sx={{
                background: "#FE4E3A",
                color: "#fff",
                width: "100%",
                fontWeight: 600,
                mt: 1,
                "&:hover": {
                  background: "#FE4E3A",
                  color: "#fff",
                },
              }}
              onClick={() => setConfirmingPlan(plan)}
              disabled={!!loadingPlanId}
            >
              {loadingPlanId === plan.id ? "Redirecting..." : "Choose Plan"}
            </Button>
          )}
        </Paper>
      </Box>
    );
  };

  // Include Free plan in the feature table
  const FEATURE_PLAN_ORDER = [
    "VoiceCare Free Plan",
    "VoiceCare Startup Plan",
    "VoiceCare Pro Plan",
    "VoiceCare Scale Plan",
    "VoiceCare Enterprise Plan",
  ];

  // Map feature keys to their categories in the JSON
  const FEATURE_CATEGORY_MAP = {
    voice_ai_agent: "core",
    voice_ai_assist: "core",
    role_based_access: "security",
    location_based_access: "security",
    soc2_hipaa: "security",
    hitrust_cloud: "security",
    data_retention: "data",
    in_app_support: "support",
    email_support: "support",
    dedicated_support: "support",
    sla_response_time: "support",
    prebuilt_dashboards: "reporting",
    dedicated_implementation_manager: "implementation",
    technical_account_manager: "implementation",
    founder_quarterly_reviews: "implementation",
  };

  // Find the index of the current plan for column highlighting
  const currentPlanIndex = FEATURE_PLAN_ORDER.findIndex(
    (name) => name === currentPlan
  );

  const borderBottomStroke = themeMode === "dark" ? "#252144" : "#e2e2e2";

  // Feature display name mapping for proper formatting
  const FEATURE_DISPLAY_NAMES = {
    voice_ai_agent: "Voice AI Agent",
    voice_ai_assist: "Voice AI Assist",
    soc2_hipaa: "SOC 2 Type II & HIPPA",
    hitrust_cloud: "HiTRUST Cloud",
    sla_response_time: "SLA Response Time",
  };

  const renderFeatureComparison = () => {
    const featureCategories = [
      {
        title: "Core Features",
        features: ["voice_ai_agent", "voice_ai_assist"],
      },
      {
        title: "Security & Compliance",
        features: [
          "role_based_access",
          "location_based_access",
          "soc2_hipaa",
          "hitrust_cloud",
        ],
      },
      { title: "Data", features: ["data_retention"] },
      {
        title: "Support",
        features: [
          "in_app_support",
          "email_support",
          "dedicated_support",
          "sla_response_time",
        ],
      },
      { title: "Reporting", features: ["prebuilt_dashboards"] },
      {
        title: "Implementation",
        features: [
          "dedicated_implementation_manager",
          "technical_account_manager",
          "founder_quarterly_reviews",
        ],
      },
    ];
    const allFeatures = featureCategories.flatMap((cat) => cat.features);
    const lastFeatureIndex = allFeatures.length - 1;

    return (
      <Box sx={{ mt: 4, mb: 8, width: "100%", overflowX: "auto" }}>
        <Typography
          variant="h4"
          sx={{
            color: themeMode === "dark" ? "#ffffff" : "#000000",
            fontWeight: 700,
            mb: 4,
            letterSpacing: 1,
            textAlign: "left",
          }}
        >
          Compare all features
        </Typography>
        {/* <Divider sx={{ borderColor: '#3B3757', mb: 5 }} /> */}
        <Grid container spacing={0} sx={{ minWidth: 1200 }}>
          <Grid item xs={3} />
          {FEATURE_PLAN_ORDER.map((planName, idx) => {
            const plan = plans.find((p) => p.name === planName);
            if (!plan) return null;
            const isCurrentPlan = idx === currentPlanIndex;
            return (
              <Grid
                item
                xs={1.8}
                key={plan.id}
                sx={{
                  textAlign: "center",
                  py: 2,
                  minWidth: 130,
                  maxWidth: 180,
                  fontWeight: 700,
                  color: "#FF5C5C",
                  fontSize: "1.1rem",
                  borderBottom: isCurrentPlan
                    ? "2px solid #FF5C5C"
                    : `1px solid ${borderBottomStroke}`,
                  background: isCurrentPlan
                    ? "rgba(255, 92, 92, 0.05)"
                    : "transparent",
                  zIndex: isCurrentPlan ? 2 : 1,
                }}
              >
                {getPlanDisplayName(plan.name)}
              </Grid>
            );
          })}
        </Grid>
        {featureCategories.map((category, catIdx) => (
          <Box key={catIdx} sx={{ mb: 2 }}>
            <Grid container spacing={0} alignItems="center">
              <Grid item xs={12} sx={{ mb: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#FF5C5C",
                    fontWeight: 600,
                    mt: 2,
                    mb: 1,
                    pl: 1,
                  }}
                >
                  {category.title}
                </Typography>
              </Grid>
              {category.features.map((feature, idx) => {
                const absFeatureIdx = allFeatures.findIndex(
                  (f, i) =>
                    i >= 0 &&
                    f === feature &&
                    allFeatures.slice(0, i).filter((x) => x === feature)
                      .length === 0
                );
                return (
                  <React.Fragment key={idx}>
                    <Grid
                      item
                      xs={3}
                      sx={{
                        pl: 2,
                        py: 2,
                        borderBottom: `1px solid ${borderBottomStroke}`,
                        background: "rgba(255,255,255,0.01)",
                        minWidth: 240,
                        wordBreak: "break-word",
                        hyphens: "auto",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          color: `${
                            themeMode === "dark" ? "#ffffff" : "#000000"
                          }`,
                          fontWeight: 500,
                        }}
                      >
                        {FEATURE_DISPLAY_NAMES[feature] || 
                          feature
                            .split("_")
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() + word.slice(1)
                            )
                            .join(" ")}
                      </Typography>
                    </Grid>
                    {FEATURE_PLAN_ORDER.map((planName, idxPlan) => {
                      const plan = plans.find((p) => p.name === planName);
                      if (!plan) return null;
                      const details =
                        planFeatures.plans[
                          PLAN_NAME_MAP[planName] || planName
                        ] || {};
                      const categoryKey = FEATURE_CATEGORY_MAP[feature];
                      const featureValue =
                        details.features && details.features[categoryKey]
                          ? details.features[categoryKey][feature]
                          : undefined;
                      const isCurrentPlan = idxPlan === currentPlanIndex;
                      return (
                        <Grid
                          item
                          xs={1.8}
                          key={plan.id + feature}
                          sx={{
                            textAlign: "center",
                            py: 2,
                            minWidth: 130,
                            maxWidth: 180,
                            borderBottom: `1px solid ${borderBottomStroke}`,
                            background: isCurrentPlan
                              ? `${
                                  themeMode === "dark" ? "#151138" : "#e2e2e2"
                                }`
                              : "rgba(255,255,255,0.01)",
                            zIndex: isCurrentPlan ? 2 : 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: `${
                                themeMode === "dark" ? "#ffffff" : "#000000"
                              }`,
                            }}
                          >
                             
                            {featureValue === true ? (
                              <Box
                                sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: "50%",
                                  background: "#FF5C5C",
                                  color: "#ffffff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "1rem",
                                }}
                              >
                                ✔
                              </Box>
                            ) : featureValue === false ? (
                              <Box
                                sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: "50%",
                                  background: "#28224B",
                                  color: "#ffffff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "1rem",
                                  opacity: 0.7,
                                }}
                              >
                                ✖
                              </Box>
                            ) : (
                              featureValue || "-"
                            )}
                          </Box>
                        </Grid>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </Grid>
          </Box>
        ))}
      </Box>
    );
  };

  // Confirmation popup for choosing a plan
  const renderPlanConfirmationPopup = () => {
    if (!confirmingPlan) return null;
    // Theme-based styling (from BillingRestrictionPopup.js)
    const overlayStyle = {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      backdropFilter: "blur(4px)",
      WebkitBackdropFilter: "blur(4px)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 10000,
      animation: "fadeIn 0.3s ease-out",
    };
    const popupStyle = {
      backgroundColor: themeMode === "dark" ? "#04002b" : "#ffffff",
      color: currentTheme.homePage?.textColor || "#222",
      borderRadius: "16px",
      padding: "0",
      maxWidth: "500px",
      width: "90%",
      maxHeight: "90vh",
      overflow: "hidden",
      boxShadow:
        themeMode === "dark"
          ? "0 20px 40px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.1)"
          : "0 20px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)",
      border: themeMode === "dark" ? "1px solid #302d4c" : "1px solid #e9e8ea",
      animation: "slideIn 0.3s ease-out",
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif",
    };
    const headerStyle = {
      padding: "24px 24px 0 24px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      borderBottom: `1px solid ${themeMode === "dark" ? "#302d4c" : "#e9e8ea"}`,
      paddingBottom: "16px",
      marginBottom: "24px",
    };
    const titleStyle = {
      fontSize: "20px",
      fontWeight: "600",
      color: "#ff8c00",
      margin: "0",
      display: "flex",
      alignItems: "center",
      gap: "12px",
    };
    const closeButtonStyle = {
      background: "none",
      border: "none",
      fontSize: "24px",
      color: currentTheme.homePage?.textColor || "#222",
      cursor: "pointer",
      padding: "4px",
      borderRadius: "4px",
      opacity: 0.7,
      transition: "opacity 0.2s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "32px",
      height: "32px",
    };
    const contentStyle = {
      padding: "0 24px",
      marginBottom: "32px",
    };
    const messageStyle = {
      fontSize: "16px",
      lineHeight: "1.6",
      color: currentTheme.homePage?.textColor || "#222",
      margin: "0",
      textAlign: "left",
    };
    const footerStyle = {
      padding: "0 24px 24px 24px",
      display: "flex",
      justifyContent: "flex-end",
      gap: "12px",
    };
    const buttonBaseStyle = {
      padding: "12px 24px",
      borderRadius: "8px",
      border: "none",
      fontSize: "14px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.2s ease",
      minWidth: "80px",
      height: "44px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    };
    const primaryButtonStyle = {
      ...buttonBaseStyle,
      backgroundColor: "#e63e2b",
      color: "#ffffff",
    };
    const secondaryButtonStyle = {
      ...buttonBaseStyle,
      backgroundColor: themeMode === "dark" ? "#302d4c" : "#e9e8ea",
      color: currentTheme.homePage?.textColor || "#222",
    };
    return (
      <>
        <style>
          {`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes slideIn {
              from { 
                opacity: 0; 
                transform: translateY(-20px) scale(0.95); 
              }
              to { 
                opacity: 1; 
                transform: translateY(0) scale(1); 
              }
            }
          `}
        </style>
        <div style={overlayStyle} onClick={() => setConfirmingPlan(null)}>
          <div style={popupStyle} onClick={(e) => e.stopPropagation()}>
            <div style={headerStyle}>
              <h2 style={titleStyle}>
                {/* Warning icon */}
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2L1 21h22L12 2zm0 3.99L19.53 19H4.47L12 5.99zM11 10v4h2v-4h-2zm0 6v2h2v-2h-2z"
                    fill="#ff8c00"
                  />
                </svg>
                Plan Change Confirmation
              </h2>
              <button
                style={closeButtonStyle}
                onClick={() => setConfirmingPlan(null)}
                onMouseEnter={(e) => (e.target.style.opacity = "1")}
                onMouseLeave={(e) => (e.target.style.opacity = "0.7")}
              >
                ×
              </button>
            </div>
            <div style={contentStyle}>
              <p style={messageStyle}>
                The duration of each call is unpredictable. If your account
                exceeds the allocated units, your batch will continue
                uninterrupted, and any additional units consumed will be billed
                accordingly.
                <br />
                <br />
                <b>Do you agree to this?</b>
              </p>
            </div>
            <div style={footerStyle}>
              <button
                style={secondaryButtonStyle}
                onClick={() => setConfirmingPlan(null)}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor =
                    themeMode === "dark" ? "#404040" : "#d0d0d0")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor =
                    themeMode === "dark" ? "#302d4c" : "#e9e8ea")
                }
              >
                No
              </button>
              <button
                style={primaryButtonStyle}
                onClick={async () => {
                  setConfirmingPlan(null);
                  await handleChoosePlan(confirmingPlan);
                }}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = "#d32f2f")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor = "#e63e2b")
                }
              >
                Yes, I Agree
              </button>
            </div>
          </div>
        </div>
      </>
    );
  };

  // Only show a single row of cards, allow wrapping on small screens
  return (
    <Box>
      {plansLoading && <LogoLoader />}
      {!plansLoading && (
        <>
          {error && (
            <Box
              sx={{
                color: "#FF5C5C",
                mb: 2,
                textAlign: "center",
                fontWeight: 500,
              }}
            >
              {error}
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: { xs: "wrap", md: "nowrap" },
              justifyContent: { xs: "center", md: "center" },
              alignItems: "stretch",
              width: "100%",
              mb: 8,
              pb: { xs: 2, md: 0 },
            }}
          >
            {filteredPlans.map(renderPlanCard)}
          </Box>
          {renderFeatureComparison()}
        </>
      )}

      <EnterpriseContactModal
        open={showEnterpriseModal}
        onClose={handleCloseEnterpriseModal}
      />
      {renderPlanConfirmationPopup()}
    </Box>
  );
};

export default PlansAndLimits;
